import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Heart } from 'lucide-react';
import { usePatient } from '@/hooks/queries';
import { dietRestrictionsDisplay, motivationsDisplay } from './patientTags';

/**
 * Compact full-width strip that surfaces the patient's motivations + dietary
 * restrictions directly under the name in the global header. Portaled into
 * `#app-header-below`, so it spans the whole header width (under the name and
 * across to "Log out"). Only mounted when the tag-placement preference is
 * 'header' (see PatientShell); the rail hides its own copy in that mode.
 *
 * Reads the same shared formatters as the rail so both placements show
 * identical text. `usePatient` is deduped by TanStack Query — no extra fetch.
 */
export const PatientTagsHeaderRow = ({ patientId }: { patientId: number }) => {
  const { t } = useTranslation();
  const patient = usePatient(patientId).data;

  const motivations = useMemo(() => motivationsDisplay(patient?.motivations), [patient]);
  const restrictions = useMemo(
    () => dietRestrictionsDisplay(patient?.diet_restrictions, t('None')),
    [patient, t],
  );

  // Nothing to show until the patient loads — keep the strip collapsed rather
  // than flashing two em-dashes.
  if (!patient) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-0.5 py-1 text-xs">
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <Heart className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="font-medium text-foreground">{t('Motivations')}:</span>
        <span className="truncate text-muted-foreground">{motivations}</span>
      </span>
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="font-medium text-foreground">{t('Dietary Restrictions')}:</span>
        <span className="truncate text-muted-foreground">{restrictions}</span>
      </span>
    </div>
  );
};
